import { updateCache } from "@/services/backend/_lib/update-cache";
import { DocumentState } from "@/services/backend/documents/types/state";
import {
  ReferenceNumberFilters,
  ResetReferenceNumberRequest,
} from "@/services/backend/documents/types/reference-number";
import { backendApi } from "@/services/backend/api";
import { Offer } from "@/services/backend/htz/offers/types/offer";
import { Filter } from "@/shared/lib/filter/filter";
import {
  providesEntity,
  providesList,
} from "@/services/backend/_lib/tag-helpers";
import { downloadResponseHandler } from "@/services/backend/_lib/pdf-helpers";
import {
  objectURLResponseHandler,
  revokeObjectURLOptions,
} from "@/shared/lib/utilities/object-url";
import {
  DocumentBaseFilters,
  DocumentCommonFilters,
} from "@/services/backend/documents/types/filters";
import { OrdrBy } from "@/shared/lib/ordr/ordrBy";
import { Lst } from "@/shared/lib/lst/lst";
import { Address } from "@/services/backend/addresses/types/address";
import { Representative } from "@/services/backend/representatives/types/representative";
import { PositionPatch } from "@/services/backend/htz/position/types/position";

// invalidatesOffer invalidates the offer of a request changing it.
const invalidatesOffer = (
  _result: unknown,
  _error: unknown,
  request: { id: string },
) => [
  { type: "HtzOffer" as const, id: request.id },
  { type: "HtzOffer" as const, id: "LIST" },
];

const offerApi = backendApi.injectEndpoints({
  endpoints: (build) => ({
    htzOfferCreate: build.mutation<Offer, CreateRequest>({
      query: (request) => ({
        url: `/htz/offer`,
        method: "POST",
        body: request,
      }),
      invalidatesTags: invalidatesOffer,
    }),
    htzOfferAddPosition: build.mutation<Offer, AddPositionRequest>({
      query: (request) => ({
        url: `/htz/offer/position`,
        method: "POST",
        body: request,
      }),
      invalidatesTags: invalidatesOffer,
    }),
    htzOfferPatchPosition: build.mutation<Offer, PatchPositionRequest>({
      query: (request) => ({
        url: `/htz/offer/position`,
        method: "PATCH",
        body: request,
      }),
      invalidatesTags: invalidatesOffer,
    }),
    htzOfferRemovePosition: build.mutation<Offer, RemovePositionRequest>({
      query: (request) => ({
        url: `/htz/offer/position`,
        method: "DELETE",
        body: request,
      }),
      invalidatesTags: invalidatesOffer,
    }),
    htzOfferShow: build.query<Offer, ShowRequest>({
      query: (request) => ({
        url: `/htz/offer`,
        method: "GET",
        params: request,
      }),
      providesTags: providesEntity("HtzOffer"),
    }),
    htzOfferList: build.query<Lst<Offer>, ListRequest>({
      query: (request) => ({
        url: `/htz/offer/list`,
        method: "GET",
        params: {
          request: btoa(JSON.stringify(request)),
        },
      }),
      providesTags: (result) => providesList(result?.items, "HtzOffer"),
    }),
    htzOfferPatch: build.mutation<Offer, PatchRequest>({
      query: (request) => ({
        url: `/htz/offer`,
        method: "PATCH",
        body: request,
      }),
      invalidatesTags: (_result, _error, request) => [
        { type: "HtzOffer", id: request.id },
        { type: "HtzOffer", id: "LIST" },
      ],
    }),
    htzOfferResetReferenceNumber: build.mutation<
      Offer,
      ResetReferenceNumberRequest
    >({
      query: (request) => ({
        url: `/htz/offer/reset-reference-number`,
        method: "PATCH",
        body: request,
      }),
      invalidatesTags: (_result, _e, request) => [
        { type: "HtzOffer", id: request.id },
        { type: "HtzOffer", id: "LIST" },
      ],
    }),
    htzOfferUpdateState: build.mutation<Offer, UpdateStateRequest>({
      query: (request) => ({
        url: `/htz/offer/state`,
        method: "PATCH",
        body: request,
      }),
      invalidatesTags: (_r, _e, request) => [
        { type: "HtzOffer", id: request.id },
        "HtzOffer",
      ],
    }),
    htzOfferDelete: build.mutation<null, DeleteRequest>({
      query: (request) => ({
        url: `/htz/offer`,
        method: "DELETE",
        body: request,
      }),
      invalidatesTags: ["HtzOffer"],
    }),
    htzOfferPdfPreview: build.query<{ objectURL: string }, PreviewPDFRequest>({
      ...revokeObjectURLOptions(),
      query: (request) => ({
        url: `/htz/offer`,
        method: "GET",
        params: request,
        headers: { Accept: "application/pdf" },
        responseHandler: objectURLResponseHandler,
        // Keep the PDF out of the browser's HTTP cache.
        cache: "no-store",
      }),
    }),
    htzOfferPdfDownload: build.mutation<
      { filename: string; objectURL: string },
      PDFRequest
    >({
      query: (request) => ({
        url: `/htz/offer`,
        method: "GET",
        params: { ...request, download: true },
        headers: { Accept: "application/pdf" },
        responseHandler: downloadResponseHandler,
        // Keep the PDF out of the browser's HTTP cache.
        cache: "no-store",
      }),
      invalidatesTags: (_r, _e, request) => [
        { type: "HtzOffer", id: request.offerId },
      ],
    }),
  }),
});

export const {
  useHtzOfferCreateMutation,
  useHtzOfferAddPositionMutation,
  useHtzOfferPatchPositionMutation,
  useHtzOfferRemovePositionMutation,
  useHtzOfferShowQuery,
  useHtzOfferPatchMutation,
  useHtzOfferResetReferenceNumberMutation,
  useHtzOfferListQuery,
  useLazyHtzOfferListQuery,
  useHtzOfferUpdateStateMutation,
  useHtzOfferDeleteMutation,
  useLazyHtzOfferPdfPreviewQuery,
  useHtzOfferPdfDownloadMutation,
} = offerApi;

/**
 * CreateRequest identifies a new offer. Everything else is a default or set
 * with a {@link PatchRequest}.
 */
export interface CreateRequest {
  id: string;
  processId: string;
  companyId: string;
  branchId: string;
  customerId: string;
  serviceObjectId: string;
}

// PatchRequest updates the document. All fields except `id` are optional:
// an omitted key leaves the corresponding value unchanged, null clears it.
// The positions are changed on their own.
export interface PatchRequest {
  id: string;
  referenceNumber?: string;
  date?: string | null;
  specialAgreements?: string;
  postalAddress?: Address;
  serviceAddress?: Address;
  representative?: Representative;
}

// AddPositionRequest adds the position `positionId` to the offer `id`,
// prefilled with the patch, e.g. from a catalog item.
export interface AddPositionRequest extends PositionPatch {
  id: string;
  positionId: string;
}

// PatchPositionRequest updates the position `positionId` of the offer `id`.
export interface PatchPositionRequest extends PositionPatch {
  id: string;
  positionId: string;
}

export interface RemovePositionRequest {
  id: string;
  positionId: string;
}

export interface ShowRequest {
  id: string;
}

export interface ListRequest
  extends DocumentBaseFilters, DocumentCommonFilters, ReferenceNumberFilters {
  orderBy?: OrdrBy<string>[];
  date?: Filter<string>;
  state?: Filter<DocumentState>;
  limit?: number;
  offset?: number;
}

export interface List {
  items: Offer[];
  count: number;
}

export interface UpdateStateRequest {
  id: string;
  state: DocumentState;
  stateSetBy: string;
}

export interface DeleteRequest {
  id: string;
}

export enum OfferDocument {
  ALL = "all",
  OFFER = "offer",
  ORDER = "order",
}

export interface PreviewPDFRequest {
  offerId: string;
  document: OfferDocument;
}

export interface PDFRequest {
  offerId: string;
  document: OfferDocument;
  download: boolean;
}

export const updateHtzOfferCache = updateCache(offerApi, "htzOfferShow");
